import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Typography, Image, App, Alert } from "antd";
import Logo from "../../assets/images/auth-logo.png";
import {
  sendSlaEsignRequest,
  getSlaSignStatus,
} from "../../redux/slices/EsignSlice";
import { getUserLocation } from "../../helpers/helpers";
import CustomButton from "../../components/CustomButton/Button";
import Loader from "../../components/CustomLoader/Loader";
import "./Sla.scss";

const { Text, Title } = Typography;

const Sla = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { message, modal } = App.useApp();
  const { loading, checkStatusLoading } = useSelector((state) => state.esign);
  const [docData, setDocData] = useState({
    documentId: null,
    docketId: null,
  });
  const { documentId, docketId } = docData;
  const [userLocation, setUserLocation] = useState(null);
  const [locationLoading, setLocationLoading] = useState(false);

  const openAgreement = (link) => {
    window.open(link, "_blank", "noopener,noreferrer");
    message.success(
      "SLA opened in a new tab. Please complete the signing process.",
    );
  };

  const showAgreementModal = (link) => {
    modal.confirm({
      title: "Agreement Ready",
      content:
        "Your Service Level Agreement has been generated successfully. Click Open Agreement to continue.",
      okText: "Open Agreement",
      cancelText: "Cancel",
      onOk: () => {
        openAgreement(link);
      },
    });
  };

  const signSlaRequest = async () => {
    try {
      let location = userLocation;

      if (!location) {
        setLocationLoading(true);
        const locationData = await getUserLocation();
        location = locationData?.city || "";
        setUserLocation(location);
        setLocationLoading(false);
      }
      const response = await dispatch(sendSlaEsignRequest(location)).unwrap();

      if (
        response?.invitation_link &&
        response?.document_id &&
        response?.docket_id
      ) {
        setDocData({
          documentId: response.document_id,
          docketId: response.docket_id,
        });

        showAgreementModal(response.invitation_link);
      }
    } catch (error) {
      message.error("Failed to generate SLA link.");
    } finally {
      setLocationLoading(false);
    }
  };

  const checkSlaStatus = async () => {
    if (!documentId || !docketId) {
      message.error(
        "No valid document ID found. Please send the request first.",
      );
      return;
    }

    try {
      const { response } = await dispatch(
        getSlaSignStatus({ document_id: documentId, docket_id: docketId }),
      ).unwrap();

      if (response?.status === "signed") {
        message.success("SLA signed successfully!");
        navigate("/board-resolution");
      } else if (response?.status === "unsigned") {
        message.warning(
          "SLA not signed yet. Please complete the sign process.",
        );
      } else {
        message.warning("SLA status unknown. Please check again later.");
      }
    } catch (error) {
      message.error("Failed to check SLA status.");
    }
  };

  useEffect(() => {
    const handleTabFocus = () => {
      if (document.visibilityState === "visible" && documentId && docketId) {
        checkSlaStatus();
      }
    };

    document.addEventListener("visibilitychange", handleTabFocus);
    return () => {
      document.removeEventListener("visibilitychange", handleTabFocus);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentId, docketId]);

  return (
    <div className="sla-container">
      <div className="logo">
        <Image preview={false} src={Logo} alt="Logo" />
      </div>

      <Alert
        message={
          <div>
            If your browser is blocking the signing page from opening in a new
            tab,
            <br />
            please allow pop-ups for this website in your browser settings and
            try again.
          </div>
        }
        type="info"
        showIcon
        className="info-alert"
      />

      {(checkStatusLoading || locationLoading) && <Loader />}
      <div className="sla-content">
        <Title level={4} style={{ marginBottom: 10 }}>
          Service Level Agreement (SLA)
        </Title>
        <Text type="secondary">
          To proceed with your account setup, please sign the SLA document.
        </Text>
        <CustomButton
          loading={loading}
          type="primary"
          style={{ marginTop: 10 }}
          onClick={signSlaRequest}
        >
          Sign SLA
        </CustomButton>
        <CustomButton
          style={{ marginTop: 16 }}
          variant="link"
          onClick={() => navigate("/onboarding")}
        >
          Maybe Later
        </CustomButton>
      </div>
    </div>
  );
};

export default Sla;
